import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import formService from '../../services/formService';

const Field = ({ field, value, onChange }) => {
  const common = {
    id: field.key,
    name: field.key,
    required: field.required,
    placeholder: field.placeholder ? `${field.placeholder}${field.placeholder_tc ? ' / ' + field.placeholder_tc : ''}` : '',
    className: 'form-control'
  };
  
  // Helper to get bilingual option label
  const getOptionLabel = (opt, index) => {
    const tcOpt = field.options_tc && field.options_tc[index];
    return tcOpt ? `${opt} ${tcOpt}` : opt;
  };
  
  if (field.type === 'text' || field.type === 'email' || field.type === 'date') {
    return <input type={field.type} {...common} value={value || ''} onChange={e=>onChange(field.key, e.target.value)} />
  }
  if (field.type === 'number') {
    return <input type="number" {...common} value={value ?? ''} onChange={e=>{
      const val = e.target.value;
      onChange(field.key, val === '' ? '' : Number(val));
    }} />
  }
  if (field.type === 'textarea') {
    return <textarea {...common} rows={4} value={value || ''} onChange={e=>onChange(field.key, e.target.value)} />
  }
  if (field.type === 'select') {
    return (
      <select {...common} value={value || ''} onChange={e=>onChange(field.key, e.target.value)}>
        <option value="">-- Select 請選擇 --</option>
        {(field.options || []).map((opt, idx) => <option key={opt} value={opt}>{getOptionLabel(opt, idx)}</option>)}
      </select>
    );
  }
  if (field.type === 'multiselect') {
    return (
      <select {...common} multiple value={Array.isArray(value) ? value : []} onChange={e=>{
        const selected = Array.from(e.target.selectedOptions).map(o=>o.value);
        onChange(field.key, selected);
      }}>
        {(field.options || []).map((opt, idx) => <option key={opt} value={opt}>{getOptionLabel(opt, idx)}</option>)}
      </select>
    );
  }
  if (field.type === 'radio') {
    return (
      <div className="space-y-1">
        {(field.options || []).map((opt, idx) => (
          <label key={opt} className="inline-flex items-center mr-4">
            <input
              type="radio"
              name={field.key}
              value={opt}
              checked={value === opt}
              onChange={()=>onChange(field.key, opt)}
            />
            <span className="ml-2">{getOptionLabel(opt, idx)}</span>
          </label>
        ))}
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <input type="checkbox" checked={!!value} onChange={e=>onChange(field.key, e.target.checked)} />
    );
  }
  if (field.type === 'file') {
    return (
      <input type="file" onChange={async e=>{
        const file = e.target.files && e.target.files[0];
        if (!file) { onChange(field.key, null); return; }
        // Store lightweight metadata for now
        onChange(field.key, { name: file.name, size: file.size, type: file.type });
      }} />
    );
  }
  return <input type="text" {...common} value={value || ''} onChange={e=>onChange(field.key, e.target.value)} />
};

const FormRender = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const f = await formService.getBySlug(slug);
        setForm(f);
      } catch (e) {
        setMsg('Form not found or unpublished');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const onChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      await formService.submitBySlug(slug, { data: values });
      setSubmitted(true);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to submit');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!form) return <div className="p-6 text-red-600">{msg}</div>;

  const now = new Date();
  const notOpen = form.open_at && new Date(form.open_at) > now;
  const closed = form.close_at && new Date(form.close_at) < now;
  const blocked = notOpen || closed;

  const fields = (form.schema?.fields || []);
  
  // Helper to get bilingual label
  const getLabel = (label, labelTc) => {
    if (label && labelTc) return `${label} ${labelTc}`;
    return label || labelTc || '';
  };
  
  // Get bilingual form title
  const formTitle = getLabel(form.schema?.title, form.schema?.title_tc) || form.name;

  // Show success message after submission
  if (submitted) {
    return (
      <div className="p-6 max-w-2xl text-center">
        <div className="success-icon mb-4">
          <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
        </div>
        <h2 className="text-2xl font-semibold mb-3">Submitted Successfully! 提交成功！</h2>
        <p className="text-lg text-muted">Thank you for your submission. 感謝您的提交。</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-2xl font-semibold mb-4">{formTitle}</h2>
      {msg && <div className="mb-2 text-sm text-danger">{msg}</div>}
      {blocked && (
        <div className="mb-3 text-sm text-red-600">
          {notOpen && 'This form is not open yet. 表單尚未開放。'}
          {closed && 'This form is closed. 表單已關閉。'}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        {fields.map(f => (
          <div key={f.key}>
            <label className="block text-sm font-medium mb-1">
              {getLabel(f.label, f.label_tc)}{f.required && ' *'}
            </label>
            <Field field={f} value={values[f.key]} onChange={onChange} />
          </div>
        ))}
        <button type="submit" className="btn btn-primary" disabled={blocked}>Submit 提交</button>
      </form>
    </div>
  );
};

export default FormRender;
